'use client';

import { motion } from 'framer-motion';
import type { ReactElement } from 'react';

import { BrandIcon } from '@/components/brand-icon';

interface Props {
  /** Full-screen (cobre o viewport inteiro) ou inline (preenche o parent). */
  fullScreen?: boolean;
  /** Tamanho do ícone em px (default 64 full-screen, 40 inline). */
  iconSize?: number;
  /** Largura da barra em px (default 200 full-screen, 140 inline). */
  barWidth?: number;
}

/**
 * Loader visual do Trakd: ícone com pulse + barra de progresso animada com
 * gradiente brand. Substitui os `<div>Carregando…</div>` feios em Suspense
 * fallbacks.
 *
 * A barra usa duas animações sobrepostas:
 *  1. Track (background sutil) com gradient brand 100% width
 *  2. Indicator (overlay com mask) que desliza horizontalmente em loop
 * Resultado: aparência de barra "preenchendo" continuamente — sem fim claro,
 * sinaliza loading indeterminado sem ficar repetitivo.
 */
export function TrakdLoader({ fullScreen = true, iconSize, barWidth }: Props = {}): ReactElement {
  const finalIconSize = iconSize ?? (fullScreen ? 64 : 40);
  const finalBarWidth = barWidth ?? (fullScreen ? 200 : 140);

  return (
    <div
      className={
        fullScreen
          ? 'fixed inset-0 z-50 flex items-center justify-center'
          : 'flex w-full items-center justify-center py-12'
      }
      role="status"
      aria-label="Carregando"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94, filter: 'blur(8px)' }}
        animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col items-center gap-6"
      >
        <motion.div
          style={{
            width: finalIconSize,
            height: finalIconSize,
            borderRadius: '22%',
          }}
          animate={{
            scale: [1, 1.06, 1],
            filter: [
              'drop-shadow(0 0 0px rgba(144, 97, 255, 0))',
              'drop-shadow(0 0 24px rgba(144, 97, 255, 0.55))',
              'drop-shadow(0 0 0px rgba(144, 97, 255, 0))',
            ],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <BrandIcon size={finalIconSize} alt="Trakd" className="rounded-[22%]" />
        </motion.div>

        <div
          className="relative overflow-hidden rounded-full bg-white/[0.06]"
          style={{ width: finalBarWidth, height: 4 }}
          aria-hidden
        >
          {/* Indicator: barra de gradiente que desliza esquerda → direita em loop */}
          <motion.div
            className="absolute top-0 bottom-0 rounded-full"
            style={{
              width: '40%',
              background:
                'linear-gradient(90deg, transparent 0%, oklch(0.62 0.22 295) 30%, oklch(0.55 0.27 330) 70%, transparent 100%)',
            }}
            animate={{
              x: ['-100%', `${(finalBarWidth / (finalBarWidth * 0.4)) * 100}%`],
            }}
            transition={{
              duration: 1.4,
              repeat: Infinity,
              ease: [0.45, 0, 0.55, 1],
            }}
          />
        </div>
      </motion.div>
    </div>
  );
}
